import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'store/redux.store'
import { authService } from 'modules/auth/auth.service'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faUser, faCog, faSignOut, faFolder, faQuestionCircle, faCommentAlt } from '@fortawesome/pro-light-svg-icons'

import { getNameInitials } from 'utils/string'
import { getSupportLink } from 'utils/links'
import { ACTION, CATEGORY, DataLayer, EVENT, GoogleAnalyticsDataLayer } from 'src/utils/google-analytics'
import { ReactComponent as VersionToggleSVG } from '../../assets/version-toggle.svg'
import { useResponsiveHotDropdown } from '../../hooks/use-responsive-hot-dropdown'
import { useToggleHotDropdown } from '../../hooks/use-toggle-hot-dropdown'
import './user.styles.scss'

const FEEDBACK_ID_BY_LANGUAGE = {
  PT_BR: 'qWdHHGlV',
  ES: 'j8MGNSDp',
  EN: 'B0y9aVAE',
  FR: 'VnoiLIZ4',
  JA: 'f9C63HgG'
}

const DEFAULT_FEEDBACK_ID = FEEDBACK_ID_BY_LANGUAGE.EN

const User = ({ tabIndex }) => {
  const { user, context } = useSelector(state => state)
  const { t } = useTranslation()
  const history = useHistory()
  const hotDropdownRef = useRef<JSX.IntrinsicElements['hot-dropdown']>(null)

  const { toggle } = useToggleHotDropdown(hotDropdownRef)

  useResponsiveHotDropdown(hotDropdownRef, { maxHeight: 526 })

  function sendUserPageInteraction(label: string) {
    const dataLayer = new DataLayer(EVENT.INTERACTION, CATEGORY.UNIVERSAL_BAR, ACTION.CLICK_MENU_PROFILE, label)

    GoogleAnalyticsDataLayer.sendPageInteraction(dataLayer)
  }

  function handleClickAvatar() {
    sendUserPageInteraction('profile')
  }

  function handleClickMyAccount() {
    sendUserPageInteraction('My_Account')
    history.push('/accounts/settings')
  }

  function handleClickExtensions() {
    sendUserPageInteraction('My_Extensions')
    window.open(process.env.URL_EXTENSIONS, '_self')
  }

  function handleClickMyPurchases() {
    sendUserPageInteraction('My_Purchases')
    window.open(process.env.URL_DRIVE, '_blank')
  }

  function handleClickFeedback() {
    sendUserPageInteraction('Give_feedback')
    const formLink = `https://hotmart1.typeform.com/to/${FEEDBACK_ID_BY_LANGUAGE[user.language] || DEFAULT_FEEDBACK_ID}`

    window.open(formLink, '_blank')
  }

  function handleClickGoBack() {
    sendUserPageInteraction('Back_to_old_platform')
    history.push('/cancel')
  }

  function handleClickHelp() {
    sendUserPageInteraction('Help')
    const supportLink = getSupportLink(user.language)

    window.open(supportLink, '_blank')
  }

  function handleClickLogout() {
    sendUserPageInteraction('Logout')
    localStorage.setItem('lastRoute', window.location.pathname)
    authService.logout()
  }

  function handleUserKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      toggle()
    }
  }

  return (
    user && (
      <hot-dropdown
        ref={hotDropdownRef}
        onKeyDown={handleUserKeyDown}
        slot="header-actions"
        class="header-user hot-dropdocked _mr-3"
        tabIndex={tabIndex}
      >
        <hot-menu-item onClick={handleClickAvatar} slot="button" class="_cursor-pointer">
          <div className="hot-application-menu__icon">
            <hot-tooltip content={user?.name} position="bottom" class="_d-flex">
              <hot-avatar class="hot-avatar--sm hot-avatar--text _rounded-circle" src={user?.profile.urlPhoto} alt="">
                <span>{getNameInitials(user?.name)}</span>
              </hot-avatar>
            </hot-tooltip>
          </div>
        </hot-menu-item>

        <div slot="menu" className="_mx-5 _my-3">
          <section className="_d-flex _align-items-center _mb-4">
            <hot-avatar class="hot-avatar--xl hot-avatar--text _rounded-circle" src={user?.profile.urlPhoto}>
              <span>{getNameInitials(user?.name)}</span>
            </hot-avatar>
            <span className="_ml-4 _font-weight-light _text-truncate _text-gray-500">
              <p className="_mb-0 _text-2 _text-truncate">{user.name}</p>
              <p className="_mb-0 _text-1 _text-truncate">{user.email}</p>
            </span>
          </section>

          <hot-dropdown-menu-item slot="menu" class="header-user__item" onClick={handleClickMyAccount}>
            <FontAwesomeIcon icon={faUser} className="_text-gray-700 _mr-4" />
            <p className="_m-0 _flex-1">{t('header.menu.user.my_account')}</p>
          </hot-dropdown-menu-item>

          <hot-dropdown-menu-item slot="menu" class="header-user__item" onClick={handleClickMyPurchases}>
            <FontAwesomeIcon icon={faFolder} className="_text-gray-700 _mr-4" />
            <p className="_m-0 _flex-1">{t('header.menu.user.my_purchases')}</p>
          </hot-dropdown-menu-item>

          {(user.profile.extensions || user.profile.extensionsFreemium) && (
            <hot-dropdown-menu-item slot="menu" class="header-user__item" onClick={handleClickExtensions}>
              <FontAwesomeIcon icon={faCog} className="_text-gray-700 _mr-4" />
              <p className="_m-0 _flex-1">{t('header.menu.user.my_extensions')}</p>
            </hot-dropdown-menu-item>
          )}

          <hot-dropdown-menu-item slot="menu" class="header-user__item" onClick={handleClickFeedback}>
            <FontAwesomeIcon icon={faCommentAlt} className="_text-gray-700 _mr-4" />
            <p className="_m-0 _flex-1">{t('header.menu.user.give_feedback')}</p>
          </hot-dropdown-menu-item>

          {context?.optInStatus?.optOutEnabled && (
            <hot-dropdown-menu-item slot="menu" class="header-user__item" onClick={handleClickGoBack}>
              <VersionToggleSVG className="_text-gray-700 _mr-4" />
              <p className="_m-0 _flex-1">{t('header.menu.user.back_to_old_version')}</p>
            </hot-dropdown-menu-item>
          )}

          <hot-dropdown-menu-item slot="menu" class="header-user__item" onClick={handleClickHelp}>
            <FontAwesomeIcon icon={faQuestionCircle} className="_text-gray-700 _mr-4" />
            <p className="_m-0 _flex-1">{t('header.menu.general.help')}</p>
          </hot-dropdown-menu-item>

          <hot-dropdown-menu-item slot="menu" class="header-user__item" onClick={handleClickLogout}>
            <FontAwesomeIcon icon={faSignOut} className="_text-gray-700 _mr-4" />
            <p className="_m-0 _flex-1">{t('header.menu.user.logout')}</p>
          </hot-dropdown-menu-item>
        </div>
      </hot-dropdown>
    )
  )
}

export default React.memo(User)
